import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "./net/session.js";

export type Locale = "en" | "zh-CN";

const COPY = {
  en: {
    brand: "Guandan",
    tagline: "Four players. Two decks. One brilliant game.",
    email: "Email",
    emailPlaceholder: "you@example.com",
    sendCode: "Send code",
    password: "Password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    createAccount: "Create account",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset password",
    sendAgain: "Send again",
    backToSignIn: "Back to sign in",
    loginHint: "Sign in with your email and password.",
    guestPlay: "Play now, no account",
    guestPlayHint: "Jump straight into a table. You can keep what you win later.",
    guestBadge: "Guest",
    guestNoEmail: "No email yet",
    guestKeepTitle: "Keep this account",
    guestKeepHint:
      "You are playing as a guest. Add an email and a password and this same account keeps your level, rating and every match you have played.",
    guestKeepCta: "Add an email",
    guestLimits: "Guests do not earn coins, join staked tables, or appear on the ladder.",
    guestClaimed: "Account saved. Everything you played is still yours.",
    registerHint: "Verify your email to create a secure account.",
    resetHint: "We’ll email a code to reset your password.",
    passwordRequired: "Set a password before signing in.",
    invalidCredentials: "Email or password is incorrect.",
    emailRegistered: "That email is already registered. Sign in or use “Forgot password?”.",
    invalidEmail: "Enter a valid email address.",
    emailUnavailable: "Email delivery is unavailable right now.",
    emailDeliveryFailed: "We couldn't deliver the code. Check the address or try again later.",
    authServiceUnavailable: "The account service is temporarily unavailable. Please try again shortly.",
    captchaRequired: "Please complete the human verification first.",
    captchaFailed: "Human verification failed. Please try again.",
    captchaUnavailable: "Human verification is still loading. Please try again.",
    invalidPassword: "Password must be 8–72 characters and contain no control characters.",
    authError: "Could not complete that account action.",
    passwordMismatch: "Passwords do not match.",
    codeSent: "Code sent to {email}",
    verify: "Enter code",
    signIn: "Sign in",
    differentEmail: "Use a different email",
    codeHint: "We’ll send a six-digit code. No password to remember.",
    invalidCodeFormat: "Enter the six-digit code from the email.",
    codeNotFound: "No active code was found. Request a new code.",
    invalidCode: "That code is incorrect. Check the email and try again.",
    codeExpired: "That code has expired. Request a new code.",
    codeLocked: "Too many incorrect attempts. Request a new code.",
    codeError: "Could not send a code just now.",
    rateLimited: "Too many codes requested. Try again in a few minutes.",
    devCode: "Development mode — tap to fill {code}",
    language: "Language",
    english: "English",
    chinese: "中文",
    lobby: "Lobby",
    ranking: "Ranking",
    profile: "Profile",
    playNow: "Play now",
    findPlayers: "Find players",
    playWithFriends: "Play with friends",
    createRoom: "Create a room",
    roomCode: "Room code",
    join: "Join",
    roomCodePlaceholder: "ROOM CODE",
    shareCode: "Share code",
    copied: "Copied",
    enablePublicSpectate: "Allow spectators",
    disablePublicSpectate: "Disable spectators",
    table: "Table",
    stake: "Stake",
    rules: "Rules",
    casual: "Casual",
    standard: "Standard",
    house: "House",
    level: "Level",
    team: "Team",
    secondsLeft: "{count} seconds left",
    out: "out",
    loadOptionsError: "Could not load table options.",
    announcementConfirm: "Got it",
    announcementKind_info: "Announcement",
    announcementKind_update: "What's new",
    announcementKind_maintenance: "Scheduled maintenance",
    searchEnded: "That search ended without a table. Try again.",
    playNowHint:
      "Opens a table you can invite friends to. Any empty seat is filled at the start.",
    queueHint: "Finds players, then everyone confirms before the deal.",
    ruleset: "Ruleset",
    coinTier: "Coin tier",
    needsCoins: "Needs {coins} coins and level {level}",
    noRoom: "No room with that code.",
    roomGone: "That room is gone.",
    startMatchError: "Could not start the match.",
    somethingWrong: "Something went wrong.",
    alreadySeated: "You are already at a table.",
    alreadyInLobby: "Leave your current room before inviting someone.",
    notEnoughStake: "Not enough coins for that stake.",
    unavailableTable: "That table setup is not available.",
    signInAgain: "Please sign in again.",
    casualHint: "No tribute between hands — the friendliest way to learn.",
    standardHint: "Competition rules with tribute and resist.",
    houseHint: "Competition rules with the three-attempts-at-A house penalty.",
    practice: "Practice",
    bronze: "Bronze",
    silver: "Silver",
    gold: "Gold",
    diamond: "Diamond",
    soundToggle: "Sound",
    audioTable: "Table",
    audioInterface: "Interface",
    audioStings: "Stings",
    account: "Account",
    sound: "Sound",
    record: "Record",
    editProfile: "Edit profile",
    save: "Save",
    cancel: "Cancel",
    signOut: "Sign out",
    nameLengthError: "Names must be 2 to 24 characters.",
    profileInvalid: "The nickname or avatar is invalid.",
    profileSaveError: "Could not save your profile. Please try again.",
    saved: "Profile saved.",
    avatarHint: "Choose one of the 24 bright table identities.",
    verified: "Verified",
    wins: "Wins",
    emailImmutable: "Email is permanently linked to this account.",
    security: "Security",
    settings: "Settings",
    stats: "Stats",
    profileTab: "Profile",
    overview: "Overview",
    playStats: "Play",
    competitionStats: "Competition",
    economyStats: "Economy",
    passwordStrength: "Use 8–72 characters.",
    changePassword: "Change password",
    setPassword: "Set password",
    currentPassword: "Current password",
    passwordSaved: "Password updated.",
    passwordSaveError: "Could not update your password.",
    dailyCoins: "Daily coins ready",
    claim: "Claim",
    comeBack: "Come back each day for more.",
    season: "Season",
    daysLeft: "{count} days left",
    endsToday: "Ends today",
    seasonClosed: "Season closed",
    topPlayers: "Top players",
    aroundYou: "Around you",
    seats: "Seats",
    waitingHost: "Waiting for the host to start.",
    leave: "Leave",
    startMatch: "Start match",
    loading: "Loading…",
    loadingProfile: "Loading profile…",
    loadingTables: "Loading tables…",
    loadingLadder: "Loading the ladder…",
    noMatches: "No matches yet this season",
    noMatchesHint: "Finish a match and you’ll be the first name here.",
    couldNotLoad: "Could not load the ladder",
    tryAgain: "Try again in a moment.",
    rating: "Rating",
    matches: "Matches",
    losses: "Losses",
    handsWon: "Hands won",
    bombs: "Bombs",
    bestLevel: "Best level",
    coins: "Coins",
    winRate: "Win rate",
    handWinRate: "Hand win rate",
    avgDuration: "Average duration",
    fastest: "Fastest",
    longest: "Longest",
    totalPlays: "Plays",
    passes: "Passes",
    firstOuts: "First outs",
    maxBombPower: "Strongest bomb",
    averageUpgrade: "Average upgrade",
    currentRating: "Current rating",
    highestRating: "Highest rating",
    recentMatches: "Recent matches",
    recentRewards: "Recent rewards",
    averageCoins: "Average coins",
    averageXp: "Average XP",
    totalXp: "XP earned",
    ratingDelta: "Rating change",
    trend: "Recent trend",
    noStats: "Finish a match to unlock your cross-match stats.",
    noRecentMatches: "No archived matches yet.",
    retry: "Retry",
    cardsPlayed: "Cards played",
    displayName: "Display name",
    avatar: "Avatar",
    soundEffects: "Sound effects",
    music: "Music",
    openFilled: "Open — filled at start",
    teamHint: "Seats 1 and 3 are partners, and so are 2 and 4.",
    backLobby: "Back to the lobby",
    lookingPlayers: "Looking for players",
    holdingSeats: "Waiting for more players for {count} more seconds.",
    fillingSeats: "Filling the remaining seats now.",
    hint: "Hint",
    hintGoOut: "This play gets you out.",
    hintHelpPartner: "Give your partner room to finish.",
    hintPreserveBomb: "Keep the bomb for a harder trick.",
    hintTakeControl: "Take control of the table.",
    hintShedCards: "Shed several cards at once.",
    hintLowCost: "This costs the least structure.",
    hintPassPartner: "Let your partner keep the lead.",
    hintSaveStrength: "Pass and save your strength.",
    hintNoPlay: "No legal play — passing is the safe choice.",
    hintOptions: "Suggested plays",
    selectionOptions: "Can form",
    selectionChoose: "Select this set",
    selectionNoOptions: "No legal set contains all selected cards. Adjust your selection.",
    reactions: "Reactions",
    reactionPicker: "Choose a reaction",
    reactionSteady: "Looking good",
    reactionClap: "Nice play",
    reactionLaugh: "Haha",
    reactionMindBlown: "Boom",
    reactionJoker: "Joker bomb",
    reactionThinking: "Thinking",
    reactionSweat: "Close one",
    reactionCry: "What a pity",
    reactionParty: "Let's go",
    pass: "Pass",
    play: "Play",
    return: "Return",
    waiting: "Waiting for the other players.",
    playHistory: "Play history",
    activityHint: "Tap for this hand’s full action log",
    noActivity: "No plays yet — waiting for the opening lead.",
    trickNumber: "Trick {count}",
    playedCards: "played {count} card(s)",
    passedAction: "passed",
    close: "Close",
    currentTrick: "Current trick",
    tablePlayers: "Players",
    completedHands: "Completed hands",
    noCompletedHands: "No completed hands yet.",
    position: "Place",
    previousTrick: "Previous trick",
    comboSingle: "Single",
    comboPair: "Pair",
    comboTriple: "Triple",
    comboFullHouse: "Full house",
    comboStraight: "Straight",
    comboConsecutivePairs: "Three consecutive pairs",
    comboConsecutiveTriples: "Two consecutive triples",
    comboStraightFlush: "Straight flush",
    comboBomb: "Bomb",
    comboJokerBomb: "Four jokers",
    counterPlay: "Counter!",
    teammateLead: "Lead passed",
    comboCompactSingle: "Single",
    comboCompactPair: "Pair",
    comboCompactTriple: "Triple",
    comboCompactFullHouse: "Full",
    comboCompactStraight: "Straight",
    comboCompactConsecutivePairs: "Pairs",
    comboCompactConsecutiveTriples: "Triples",
    comboCompactStraightFlush: "Flush",
    comboCompactBomb: "Bomb",
    comboCompactJokerBomb: "Jokers",
    yourLead: "Your lead — play anything.",
    beatOrPass: "Beat it, or pass.",
    oweCard: "You owe a card back. Pick one, ten or lower.",
    handOver: "Hand over — dealing again shortly.",
    handSettlement: "Hand result",
    teamUpgraded: "Team {team} upgrades +{count}",
    levelNow: "Now at level {level}",
    keptAtA: "Held at A",
    droppedLevel: "Dropped back to {level}",
    finishOrder: "Finish order",
    nextHandSoon: "Next hand starting",
    handRewardPending: "Crediting this hand… Rewards will appear here when confirmed.",
    handRewardCredited: "Credited",
    handRewardHistory: "Hand rewards",
    handRewardTotalHint: "Match totals include these credited rewards. They are not paid again.",
    handRewardNumber: "Hand {hand}",
    handRewardParticipation: "Participation {rate}% · reward multiplier ×{multiplier}",
    handRewardLevelUp: "Level {level} reached! +{coins} bonus coins",
    handNotRated: "Unrated",
    handStakeSettlement: "Match stake {entry} (reserved earlier) · payout {payout}. Stake included in net total.",

    matchSettling: "Preparing your match report…",
    catchingUp: "Catching up…",
    win: "You win",
    lose: "You lose",
    matchWinner: "Team {team} took the match.",
    settlement: "Match settlement",
    matchStats: "Game stats",
    gameRead: "Game read",
    playerPerformance: "Player performance",
    matchWonTitle: "Match won",
    matchLostTitle: "Match over",
    handsPlayed: "Hands",
    duration: "Duration",
    levelPath: "Level path",
    rounds: "Rounds",
    upgrade: "Upgrade",
    firstOut: "First out",
    trickWins: "Trick wins",
    playedCardsCount: "Cards played",
    passedCount: "Passes",
    bombCount: "Bombs",
    maxPlay: "Best play",
    mvp: "MVP",
    controller: "Controller",
    bomber: "Bomber",
    sprinter: "Sprinter",
    coinsEarned: "Coins",
    xpEarned: "XP",
    ratingChange: "Rating",
    noSummary: "Detailed summary is still loading.",
    continueToLobby: "Back to lobby",
    replay: "Private replay",
    shareReplay: "Share replay",
    copiedReplay: "Replay link copied",
    firstOutMoment: "{name} went out first",
    bombMoment: "{name} fired a {combo}",
    trickMoment: "{name} took the trick",
    matchMoment: "Team {team} closed the match",
    teamA: "Team A",
    teamB: "Team B",
    noReward: "—",
    tableFinished: "This table has finished.",
    yourHand: "Your hand",
    previousMoment: "Previous moment",
    nextMoment: "Next moment",

    // Navigation. `replay` above already means "Private replay" on the
    // settlement screen, so the tab needs its own key rather than a reused one.
    navReplay: "Replays",
    navFriends: "Friends",
    more: "Show more",

    // Modes
    mode: "Mode",
    ranked: "Ranked",
    goldMode: "Gold",
    rankedHint: "Free to enter. Each hand earns rewards and updates your Elo and season rank.",
    goldHint:
      "Earn rewards every hand. Match winners take double the stake; rating is untouched.",
    rankedNoStake: "Ranked tables are always free to enter.",

    // The waiting room
    waitingRoom: "Waiting room",
    matchReadyTitle: "Table found",
    roomInfo: "Room info",
    roomVisibility: "Visibility",
    roomPublic: "Spectators allowed",
    roomPrivate: "Players only",
    entryFee: "Entry",
    freeEntry: "Free",
    readyWaiting: "Waiting for everyone",
    allPlayersReady: "Everyone is ready",
    lockedRoster: "Roster locked. The match begins after the countdown.",
    ready: "Ready",
    readyUp: "I'm ready",
    cancelReady: "Not yet",
    readyCount: "{ready} of {total} ready",
    readyIn: "Starting in {count}s",
    readyDeadlineIn: "{ready}/{total} ready · {count}s left to confirm",
    readyHint: "Any unconfirmed seat is replaced before the deal.",
    emptySeat: "Open seat",
    host: "Host",
    declined: "You left that table. Nothing was charged.",
    lobbyGone: "That table has already started.",

    // Replays
    replays: "Replays",
    myReplays: "My matches",
    sharedReplays: "Shared",
    watchReplay: "Watch",
    all: "All",
    rankedFilter: "Ranked",
    goldFilter: "Gold",
    noMatchesForFilter: "No matches in this filter.",
    replayFilterHint: "Choose another filter to see more matches.",
    shared: "Shared",
    makePublic: "Share",
    makePrivate: "Unshare",
    shareLinkCopied: "Link copied",
    replayEmpty: "No finished matches yet.",
    replayEmptyHint: "Play a match and it will be here to watch.",
    replayGone: "That replay is not available.",
    replayOf: "{winner} won in {hands} hands",
    playbackSpeed: "Speed",
    playbackPlay: "Play",
    playbackPause: "Pause",
    timeline: "Timeline",
    signInPlay: "Sign in / Play",
    handNumber: "Hand {count}",
    restart: "Restart",

    // Friends
    friends: "Friends",
    feed: "Feed",
    following: "Following",
    followers: "Fans",
    follow: "Follow",
    unfollow: "Unfollow",
    mutual: "Friends",
    fan: "Fan",
    findPlayer: "Find a player",
    playerCode: "Player code",
    playerCodeHint: "Share your code so friends can find you.",
    yourCode: "Your code",
    noSuchPlayer: "No player with that code.",
    inviteToDuo: "Invite to duo",
    inviteToGold: "Invite to gold",
    inviteSent: "Invite sent",
    inviteFrom: "{name} invited you",
    inviteExpired: "That invite has expired.",
    inviteAlreadyHandled: "That invite was already taken.",
    partyFull: "That party is full.",
    followLimitReached: "You are following the maximum number of players.",
    accept: "Accept",
    decline: "Decline",
    online: "Online",
    inLobby: "In a lobby",
    inMatch: "In a match",
    offline: "Offline",
    noFriends: "No friends yet.",
    noFriendsHint: "Follow someone back and you can invite each other to play.",
    noFeed: "Nothing here yet.",
    noFeedHint: "Follow a player to see their results and shared replays.",
    followsYou: "Follows you",
  },
  "zh-CN": {
    brand: "掼蛋",
    tagline: "四位玩家，两副牌，一场过瘾的较量。",
    email: "邮箱",
    emailPlaceholder: "you@example.com",
    sendCode: "发送验证码",
    password: "密码",
    newPassword: "新密码",
    confirmPassword: "确认密码",
    createAccount: "创建账号",
    forgotPassword: "忘记密码？",
    resetPassword: "重置密码",
    sendAgain: "重新发送",
    backToSignIn: "返回登录",
    loginHint: "使用邮箱和密码登录。",
    guestPlay: "免注册，直接开局",
    guestPlayHint: "先上桌打一局，赢到的东西之后可以保存。",
    guestBadge: "访客",
    guestNoEmail: "尚未绑定邮箱",
    guestKeepTitle: "保存这个账号",
    guestKeepHint: "你正在以访客身份游戏。绑定邮箱和密码后，等级、积分和全部对局记录都会留在这个账号上。",
    guestKeepCta: "绑定邮箱",
    guestLimits: "访客不获得金币，不能进入金币桌，也不出现在排行榜上。",
    guestClaimed: "账号已保存，之前打的每一局都还在。",
    registerHint: "验证邮箱后创建安全账号。",
    resetHint: "我们会向你的邮箱发送重置验证码。",
    passwordRequired: "请先设置密码再登录。",
    invalidCredentials: "邮箱或密码不正确。",
    emailRegistered: "该邮箱已经注册，请直接登录或使用“忘记密码”。",
    invalidEmail: "请输入有效的邮箱地址。",
    emailUnavailable: "暂时无法发送邮件。",
    emailDeliveryFailed: "验证码发送失败，请检查邮箱地址或稍后再试。",
    authServiceUnavailable: "账号服务暂时不可用，请稍后再试。",
    captchaRequired: "请先完成勾选验证。",
    captchaFailed: "人机验证失败，请重试。",
    captchaUnavailable: "人机验证仍在加载，请稍后重试。",
    invalidPassword: "密码需要为8到72个字符，且不能包含控制字符。",
    authError: "账号操作未完成。",
    passwordMismatch: "两次输入的密码不一致。",
    codeSent: "验证码已发送至 {email}",
    verify: "输入验证码",
    signIn: "登录",
    differentEmail: "使用其他邮箱",
    codeHint: "我们会发送六位验证码，无需记住密码。",
    invalidCodeFormat: "请输入邮件中的六位验证码。",
    codeNotFound: "没有找到有效验证码，请重新发送。",
    invalidCode: "验证码不正确，请检查邮件后重试。",
    codeExpired: "验证码已过期，请重新发送。",
    codeLocked: "验证码错误次数过多，请重新发送。",
    codeError: "暂时无法发送验证码。",
    rateLimited: "验证码请求过于频繁，请稍后再试。",
    devCode: "开发模式 — 点击填入 {code}",
    language: "语言",
    english: "English",
    chinese: "中文",
    lobby: "大厅",
    ranking: "排行榜",
    profile: "个人资料",
    playNow: "立即开局",
    findPlayers: "寻找玩家",
    playWithFriends: "和朋友玩",
    createRoom: "创建房间",
    roomCode: "房间号",
    join: "加入",
    roomCodePlaceholder: "房间号",
    shareCode: "分享房间号",
    copied: "已复制",
    enablePublicSpectate: "开启公开观战",
    disablePublicSpectate: "关闭公开观战",
    table: "牌桌",
    stake: "底分",
    rules: "规则",
    casual: "休闲",
    standard: "标准",
    house: "竞技",
    level: "等级",
    team: "第",
    secondsLeft: "剩余 {count} 秒",
    out: "出局",
    loadOptionsError: "牌桌选项加载失败。",
    announcementConfirm: "我知道了",
    announcementKind_info: "公告",
    announcementKind_update: "更新说明",
    announcementKind_maintenance: "停机维护",
    searchEnded: "搜索结束，暂时没有找到牌桌。",
    playNowHint: "开一桌并邀请好友，空位在开局时补齐。",
    queueHint: "先匹配玩家，全员确认后再发牌。",
    ruleset: "规则",
    coinTier: "底分档位",
    needsCoins: "需要 {coins} 金币和等级 {level}",
    noRoom: "没有找到这个房间。",
    roomGone: "房间已失效。",
    startMatchError: "比赛启动失败。",
    somethingWrong: "出了点问题。",
    alreadySeated: "你已经在牌桌上了。",
    alreadyInLobby: "请先离开当前房间，再邀请好友。",
    notEnoughStake: "金币或等级不足。",
    unavailableTable: "这个牌桌配置不可用。",
    signInAgain: "请重新登录。",
    casualHint: "局间不还贡——最适合学习。",
    standardHint: "包含还贡与抗贡的竞技规则。",
    houseHint: "竞技规则，并加入上A三次失败的房规。",
    practice: "练习",
    bronze: "青铜",
    silver: "白银",
    gold: "黄金",
    diamond: "钻石",
    soundToggle: "声音",
    audioTable: "牌桌",
    audioInterface: "界面",
    audioStings: "提示音",
    account: "账号",
    sound: "声音",
    record: "战绩",
    editProfile: "编辑资料",
    save: "保存",
    cancel: "取消",
    signOut: "退出登录",
    nameLengthError: "名称需要为2到24个字符。",
    profileInvalid: "昵称或头像无效。",
    profileSaveError: "资料保存失败，请重试。",
    saved: "资料已保存。",
    avatarHint: "从24个明亮的牌桌身份中选择一个。",
    verified: "已验证",
    wins: "胜场",
    emailImmutable: "邮箱与账号永久绑定，不能修改。",
    security: "安全",
    settings: "设置",
    stats: "统计",
    profileTab: "资料",
    overview: "概览",
    playStats: "牌局",
    competitionStats: "竞技",
    economyStats: "经济",
    passwordStrength: "请输入8到72个字符。",
    changePassword: "修改密码",
    setPassword: "设置密码",
    currentPassword: "当前密码",
    passwordSaved: "密码已更新。",
    passwordSaveError: "密码更新失败。",
    dailyCoins: "每日金币已就绪",
    claim: "领取",
    comeBack: "每天回来都有新奖励。",
    season: "赛季",
    daysLeft: "还剩 {count} 天",
    endsToday: "今天结束",
    seasonClosed: "赛季已结束",
    topPlayers: "榜单前三",
    aroundYou: "你的附近",
    seats: "座位",
    waitingHost: "等待房主开始比赛。",
    leave: "离开",
    startMatch: "开始比赛",
    loading: "加载中…",
    loadingProfile: "正在加载资料…",
    loadingTables: "正在加载牌桌…",
    loadingLadder: "正在加载排行榜…",
    noMatches: "本赛季还没有比赛记录",
    noMatchesHint: "完成一场比赛，你就会成为榜首。",
    couldNotLoad: "排行榜加载失败",
    tryAgain: "请稍后再试。",
    rating: "积分",
    matches: "比赛",
    losses: "负场",
    handsWon: "胜局",
    bombs: "炸弹",
    bestLevel: "最高等级",
    coins: "金币",
    winRate: "胜率",
    handWinRate: "手胜率",
    avgDuration: "平均用时",
    fastest: "最快一局",
    longest: "最长一局",
    totalPlays: "出牌次数",
    passes: "过牌",
    firstOuts: "头游",
    maxBombPower: "最大炸弹强度",
    averageUpgrade: "平均升级",
    currentRating: "当前积分",
    highestRating: "最高积分",
    recentMatches: "最近比赛",
    recentRewards: "最近奖励",
    averageCoins: "平均金币",
    averageXp: "平均经验",
    totalXp: "获得经验",
    ratingDelta: "积分变化",
    trend: "近期走势",
    noStats: "完成一场比赛后即可查看跨局统计。",
    noRecentMatches: "还没有已归档的比赛。",
    retry: "重试",
    cardsPlayed: "出牌张数",
    displayName: "显示名称",
    avatar: "头像",
    soundEffects: "音效",
    music: "音乐",
    openFilled: "空位 — 开局时由系统补齐",
    teamHint: "1号和3号是队友，2号和4号是队友。",
    backLobby: "返回大厅",
    lookingPlayers: "正在寻找玩家",
    holdingSeats: "再等待其他玩家 {count} 秒。",
    fillingSeats: "正在补齐剩余座位。",
    hint: "提示",
    hintGoOut: "这样可以直接走完。",
    hintHelpPartner: "给队友留出走牌空间。",
    hintPreserveBomb: "把炸弹留给更关键的一墩。",
    hintTakeControl: "先把牌权拿回来。",
    hintShedCards: "一次尽量多走几张。",
    hintLowCost: "这手最不破坏牌型。",
    hintPassPartner: "让队友继续掌握牌权。",
    hintSaveStrength: "先过，保留实力。",
    hintNoPlay: "没有可出的牌，可以选择不出。",
    hintOptions: "推荐出法",
    selectionOptions: "可组成",
    selectionChoose: "补齐这组牌",
    selectionNoOptions: "没有合法牌型包含当前所选牌，请调整选择。",
    reactions: "表情",
    reactionPicker: "选择表情",
    reactionSteady: "稳了",
    reactionClap: "漂亮",
    reactionLaugh: "哈哈",
    reactionMindBlown: "炸了",
    reactionJoker: "王炸",
    reactionThinking: "想想",
    reactionSweat: "好险",
    reactionCry: "可惜",
    reactionParty: "走起",
    pass: "过牌",
    play: "出牌",
    return: "还贡",
    waiting: "等待其他玩家。",
    playHistory: "出牌记录",
    activityHint: "点击查看本手牌完整行动记录",
    noActivity: "尚未出牌，正在等待首家领出。",
    trickNumber: "第 {count} 墩",
    playedCards: "出了 {count} 张牌",
    passedAction: "过牌",
    close: "关闭",
    currentTrick: "当前一墩",
    tablePlayers: "玩家",
    completedHands: "已完成手局",
    noCompletedHands: "暂无已完成手局",
    position: "位次",
    previousTrick: "上一墩",
    comboSingle: "单张",
    comboPair: "对子",
    comboTriple: "三张",
    comboFullHouse: "三带二",
    comboStraight: "顺子",
    comboConsecutivePairs: "木板（三连对）",
    comboConsecutiveTriples: "钢板（二连三）",
    comboStraightFlush: "同花顺",
    comboBomb: "炸弹",
    comboJokerBomb: "四王炸",
    counterPlay: "反压！",
    teammateLead: "接风",
    comboCompactSingle: "单张",
    comboCompactPair: "对子",
    comboCompactTriple: "三张",
    comboCompactFullHouse: "三带二",
    comboCompactStraight: "顺子",
    comboCompactConsecutivePairs: "连对",
    comboCompactConsecutiveTriples: "钢板",
    comboCompactStraightFlush: "同花顺",
    comboCompactBomb: "炸弹",
    comboCompactJokerBomb: "王炸",
    yourLead: "你先出牌——任意牌型都可以。",
    beatOrPass: "压过它，或选择过牌。",
    oweCard: "你需要还贡，请选择一张十或更小的牌。",
    handOver: "本局结束——马上重新发牌。",
    handSettlement: "单手结算",
    teamUpgraded: "{team} 队升级 +{count}",
    levelNow: "当前等级 {level}",
    keptAtA: "本局守住 A",
    droppedLevel: "等级回退至 {level}",
    finishOrder: "出完顺序",
    nextHandSoon: "下一手即将开始",
    handRewardPending: "本手奖励入账中…确认后会自动显示。",
    handRewardCredited: "已到账",
    handRewardHistory: "每手收益",
    handRewardTotalHint: "本场总收益包含以下已到账奖励，不会重复发放。",
    handRewardNumber: "第 {hand} 手",
    handRewardParticipation: "参与度 {rate}% · 奖励系数 ×{multiplier}",
    handRewardLevelUp: "升至 {level} 级！额外获得 {coins} 金币",
    handNotRated: "不计分",
    handStakeSettlement: "整场押注 {entry}（开局已扣）· 返还 {payout}。此处显示含押注的净收益。",

    matchSettling: "正在生成本场战报…",
    catchingUp: "正在追赶牌局…",
    win: "你赢了",
    lose: "你输了",
    matchWinner: "{team}队赢下了比赛。",
    settlement: "比赛结算",
    matchStats: "游戏统计",
    gameRead: "牌局解读",
    playerPerformance: "玩家表现",
    matchWonTitle: "比赛胜利",
    matchLostTitle: "比赛结束",
    handsPlayed: "手数",
    duration: "用时",
    levelPath: "等级变化",
    rounds: "牌局",
    upgrade: "升级",
    firstOut: "头游",
    trickWins: "收墩",
    playedCardsCount: "出牌张数",
    passedCount: "过牌",
    bombCount: "炸弹",
    maxPlay: "最大牌型",
    mvp: "MVP",
    controller: "控场",
    bomber: "爆破手",
    sprinter: "冲线者",
    coinsEarned: "金币",
    xpEarned: "经验",
    ratingChange: "积分",
    noSummary: "详细结算仍在加载。",
    continueToLobby: "返回大厅",
    replay: "私有复盘",
    shareReplay: "分享复盘",
    copiedReplay: "复盘链接已复制",
    firstOutMoment: "{name}率先出完",
    bombMoment: "{name}打出{combo}",
    trickMoment: "{name}赢下这一墩",
    matchMoment: "{team}队赢下比赛",
    teamA: "A队",
    teamB: "B队",
    noReward: "—",
    tableFinished: "牌桌已结束。",
    yourHand: "你的手牌",
    previousMoment: "上一时刻",
    nextMoment: "下一时刻",

    navReplay: "回放",
    navFriends: "好友",
    more: "加载更多",

    mode: "模式",
    ranked: "排位赛",
    goldMode: "金币场",
    rankedHint: "免费参与。每手获得奖励，即时更新 Elo 与赛季排名。",
    goldHint: "每手获得奖励，整场获胜拿双倍押注；积分不受影响。",
    rankedNoStake: "排位赛无需任何金币。",

    waitingRoom: "准备房间",
    matchReadyTitle: "匹配成功",
    roomInfo: "房间信息",
    roomVisibility: "可见性",
    roomPublic: "允许公开观战",
    roomPrivate: "仅房间成员",
    entryFee: "入场金币",
    freeEntry: "免费",
    readyWaiting: "等待所有玩家准备",
    allPlayersReady: "全员准备完成",
    lockedRoster: "阵容已锁定，倒计时结束后进入牌局。",
    ready: "准备",
    readyUp: "我准备好了",
    cancelReady: "再等等",
    readyCount: "{ready}/{total} 已准备",
    readyIn: "{count} 秒后开始",
    readyDeadlineIn: "已准备 {ready}/{total} · 剩余 {count} 秒确认",
    readyHint: "未确认的座位会在发牌前由系统补足。",
    emptySeat: "空座",
    host: "房主",
    declined: "你已离开该牌桌，未扣除任何费用。",
    lobbyGone: "该牌桌已经开始。",

    replays: "回放",
    myReplays: "我的对局",
    sharedReplays: "已分享",
    watchReplay: "观看",
    all: "全部",
    rankedFilter: "排位",
    goldFilter: "金币",
    noMatchesForFilter: "这个筛选下还没有对局。",
    replayFilterHint: "换一个筛选条件试试。",
    shared: "已分享",
    makePublic: "分享",
    makePrivate: "取消分享",
    shareLinkCopied: "链接已复制",
    replayEmpty: "还没有已结束的对局。",
    replayEmptyHint: "打完一局，就能在这里回看。",
    replayGone: "该回放不可用。",
    replayOf: "{winner}队在第 {hands} 局获胜",
    playbackSpeed: "速度",
    playbackPlay: "播放",
    playbackPause: "暂停",
    timeline: "进度",
    signInPlay: "登录 / 开始游戏",
    handNumber: "第 {count} 局",
    restart: "重新播放",

    friends: "好友",
    feed: "动态",
    following: "关注",
    followers: "粉丝",
    follow: "关注",
    unfollow: "取消关注",
    mutual: "好友",
    fan: "粉丝",
    findPlayer: "查找玩家",
    playerCode: "玩家编号",
    playerCodeHint: "把你的编号分享给朋友，方便他们找到你。",
    yourCode: "你的编号",
    noSuchPlayer: "没有找到该编号的玩家。",
    inviteToDuo: "邀请组队",
    inviteToGold: "邀请金币场",
    inviteSent: "邀请已发送",
    inviteFrom: "{name} 邀请你一起玩",
    inviteExpired: "该邀请已过期。",
    inviteAlreadyHandled: "该邀请已被处理。",
    partyFull: "队伍已满。",
    followLimitReached: "关注人数已达上限。",
    accept: "接受",
    decline: "拒绝",
    online: "在线",
    inLobby: "准备中",
    inMatch: "对局中",
    offline: "离线",
    noFriends: "还没有好友。",
    noFriendsHint: "互相关注后就能邀请对方一起玩。",
    noFeed: "还没有动态。",
    noFeedHint: "关注一位玩家，即可看到他的战绩与分享的回放。",
    followsYou: "关注了你",
  },
} as const;

export const TRANSLATION_KEYS = Object.keys(COPY.en) as Array<
  keyof typeof COPY.en
>;
export function translationKeysAreComplete(): boolean {
  return TRANSLATION_KEYS.every(
    (key) => typeof COPY["zh-CN"][key] === "string",
  );
}

export type TranslationKey = keyof typeof COPY.en;
export interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  number: (value: number) => string;
  date: (value: number) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [locale, setLocale] = useState<Locale>("en");

  useEffect(() => {
    if (session.status === "signedIn")
      setLocale(session.me.user.locale ?? "en");
  }, [
    session.status,
    session.status === "signedIn" ? session.me.user.locale : null,
  ]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => {
        let text: string = COPY[locale][key] ?? COPY.en[key] ?? key;
        for (const [name, replacement] of Object.entries(params ?? {}))
          text = text.replaceAll(`{${name}}`, String(replacement));
        return text;
      },
      number: (value) => new Intl.NumberFormat(locale).format(value),
      date: (value) =>
        new Intl.DateTimeFormat(locale, {
          month: "short",
          day: "numeric",
        }).format(value),
    }),
    [locale],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (value === null)
    throw new Error("useLocale must be used inside LocaleProvider");
  return value;
}

export function localeLabel(locale: Locale): string {
  return locale === "zh-CN" ? "中文" : "EN";
}
